import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { EventLedger, LedgerKind } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";

interface Props { projectId: string; onOpenScene: (sceneId: string) => void; }

const KIND: Record<LedgerKind, { label: string; icon: IconName }> = {
  relationship: { label: "Relationship", icon: "heart" },
  "promise-setup": { label: "Setup", icon: "target" },
  "promise-payoff": { label: "Payoff", icon: "check" },
  "character-change": { label: "Change", icon: "users" },
  timeline: { label: "Event", icon: "timeline" }
};

/**
 * Event Ledger — the book's notable events in manuscript reading order (not AI
 * dates). Each entry links to its scene; timeline events with no scene anchor are
 * listed separately as "unplaced" to be anchored.
 */
export function LedgerPanel({ projectId, onOpenScene }: Props) {
  const [ledger, setLedger] = useState<EventLedger | null>(null);

  const load = useCallback(() => {
    api.eventLedger(projectId).then(setLedger).catch(() => setLedger(null));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!ledger) return <div className="intel-scroll"><p className="faint" style={{ fontSize: 13 }}>Loading…</p></div>;

  // Group consecutive events by chapter for readability.
  const byChapter: { chapter: string; events: EventLedger["events"] }[] = [];
  for (const e of ledger.events) {
    const last = byChapter[byChapter.length - 1];
    if (last && last.chapter === e.chapterTitle) last.events.push(e);
    else byChapter.push({ chapter: e.chapterTitle || "—", events: [e] });
  }

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="timeline" size={16} style={{ color: "var(--accent)" }} />
          <h3>Event Ledger</h3>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 10px" }}>
          Everything notable that happens, in manuscript reading order — relationship changes, setups/payoffs, character changes, and anchored timeline events. This is the authoritative chronology (scene order, not AI dates).
        </p>
        {ledger.events.length === 0 ? (
          <p className="faint" style={{ fontSize: 13 }}>No anchored events yet. Add relationships, promises, or timeline events anchored to scenes.</p>
        ) : (
          byChapter.map((group, gi) => (
            <div key={gi} className="ledger-chapter">
              <div className="ledger-chapter-title">{group.chapter}</div>
              {group.events.map((e, i) => (
                <button key={i} className="ledger-event" onClick={() => onOpenScene(e.sceneId)} title={e.sceneLabel}>
                  <Icon name={KIND[e.kind].icon} size={12} className="ledger-event-icon" />
                  <span className="ledger-event-kind">{KIND[e.kind].label}</span>
                  <span className="ledger-event-text">{e.text}</span>
                </button>
              ))}
            </div>
          ))
        )}
      </section>

      {ledger.unplaced.length > 0 && (
        <section className="card insight-card">
          <div className="insight-card-head">
            <Icon name="alert" size={16} style={{ color: "var(--warning)" }} />
            <h3>Unplaced events</h3>
          </div>
          <p className="faint" style={{ fontSize: 12, margin: "0 0 8px" }}>
            These timeline events have no scene anchor, so they can't be placed in reading order. Anchor each to the scene where it happens.
          </p>
          <ul className="beta-list" style={{ margin: 0 }}>
            {ledger.unplaced.map((u) => (
              <li key={u.id}>{u.title}{u.storyDate ? ` (${u.storyDate})` : ""}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
